import { useEffect, useState } from 'react'
import { api, storage, TOKEN_KEY } from '../api'
import { useFeatures } from '../features'
import { t, useI18n } from '../i18n'
import type { CatalogType } from '../protocol'
import { navigate } from '../router'
import { LangSwitch } from '../ui/LangSwitch'
import { CatalogSearch } from './Search'
import { TitleView } from './Title'

/**
 * Catalog on its own page: find a title, pick a release and create a room
 * with it. Needs the room creation token.
 */
function roomName(title: string, season?: number) {
  return season ? `${title}, ${t('catalog.season', { n: season })}` : title
}

export function CatalogPage({ title }: { title?: { type: CatalogType; id: number } }) {
  const { t } = useI18n()
  const features = useFeatures()
  const [token, setToken] = useState(() => storage.get(TOKEN_KEY) ?? '')
  const [unlocked, setUnlocked] = useState(() => !!storage.get(TOKEN_KEY))
  // Images come through the server and need a session cookie.
  useEffect(() => {
    void api.session().catch(() => {})
  }, [])

  let body
  if (features && !features.catalog) {
    body = <p className="muted">{t('catalog.unavailable')}</p>
  } else if (!unlocked) {
    body = (
      <form
        className="catalog-page__token"
        onSubmit={(e) => {
          e.preventDefault()
          if (token.trim()) setUnlocked(true)
        }}
      >
        <p>{t('catalog.tokenNeeded')}</p>
        <label className="field">
          <span>{t('home.token')}</span>
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="current-password" required />
        </label>
        <button type="submit" className="btn btn--primary" disabled={!token.trim()}>
          {t('catalog.continue')}
        </button>
      </form>
    )
  } else {
    body = (
      <>
        <CatalogSearch token={token.trim()} onPick={(it) => navigate(`/catalog/${it.type}/${it.id}`)} autoFocus={!title} />
        {title ? (
          <TitleView
            key={`${title.type}${title.id}`}
            type={title.type}
            id={title.id}
            token={token.trim()}
            actionLabel={t('catalog.createRoom')}
            onChoose={async (picked, details) => {
              const room = await api.createRoom(token.trim(), roomName(details.title, picked.catalog.season), picked)
              location.assign(`/r/${room.id}`)
            }}
            selectLabel={t('catalog.createRoomBare')}
            selectHint={t('catalog.createRoomBareHint')}
            onSelect={async (picked, details) => {
              const room = await api.createRoom(token.trim(), roomName(details.title, picked.season), { catalog: picked })
              location.assign(`/r/${room.id}`)
            }}
          />
        ) : (
          <p className="muted catalog-page__hint">{t('catalog.hint')}</p>
        )}
      </>
    )
  }

  return (
    <main className="catalog-page">
      <header className="catalog-page__bar">
        <a
          href="/"
          className="logo"
          onClick={(e) => {
            e.preventDefault()
            navigate('/')
          }}
        >
          chillio
        </a>
        <span className="catalog-page__crumb">{t('catalog.title')}</span>
        <span className="room__spacer" />
        <LangSwitch />
      </header>
      <div className="catalog-page__content">{body}</div>
    </main>
  )
}
